export const identity = {
  firstName: 'Will',
  lastName: 'Tran',
  fullName: 'Will Tran',
  title: 'Senior Technical Product Manager & AI-native Product Builder',
  tagline: 'Turns complex workflows into products people can use.',
  location: 'Ho Chi Minh City, Vietnam',
  email: 'vantranworking@gmail.com',
  website: 'https://willtran.xyz',
  linkedin: 'https://linkedin.com/in/tran-huu-khanh-van',
  github: 'https://github.com/kv0906',
  x: 'https://x.com/willtran33',
  telegram: 'https://t.me/will_thoughts',
  updated: 'October 2026',
}

export const connectLinks = [
  { label: 'GitHub', href: identity.github, icon: 'github' as const },
  { label: 'X', href: identity.x, icon: 'twitter' as const },
  { label: 'LinkedIn', href: identity.linkedin, icon: 'linkedin' as const },
  { label: 'Telegram', href: identity.telegram, icon: 'telegram' as const },
]

export const connectContent = {
  title: "Let's build something useful",
  intro:
    "I'm open to senior technical product, product leadership, and builder roles—as well as conversations with teams working through complex product and AI questions.",
  motto: 'understand the work → make the decision clear → ship with the team',
}

export const references = [
  {
    name: 'Richard Barclay',
    role: 'Global Financial Services at Amazon Web Services (AWS)',
    relationship: "Richard was Van's client",
    date: 'May 2026',
    quote:
      "Van is an incredible resource, highly intelligent and highly experienced, and very good at problem solving. It's been great to work with him on projects and see the results he delivers. Couldn't hesitate to recommend him more. Very considered and well thought through, has a structured methodology and approach to tackling unknown and unforeseen problems. Very good at dealing with complexity and ambiguity and has a great ability to simplify complex technical challenges and explain them to others.",
  },
  {
    name: 'Cong Le',
    role: 'NodeJS Developer',
    relationship: 'Cong managed Van directly at Vietcetera',
    date: 'Jul 2020',
    quote:
      'Van Tran was one of the most dedicated professionals through his work at Vietcetera. He has a very strong work ethic and unparalleled analytical and problem solving abilities. Van Tran is extremely enthusiastic about his work which is infectious. He is ready to deal with difficult situations and solve the problems on time. He always takes care of the job. On account of his mature approach to his responsibilities, I often thought of Van Tran as a professional I could always rely upon to get the job done. He is an absolutely phenomenal person.',
  },
  {
    name: 'Anh Phan',
    role: 'Strategic Partnerships & Growth · Founder @NEXT1HUB',
    relationship: 'Anh worked with Van at ALAX, on different teams',
    date: 'Jul 2020',
    quote:
      "Van is a very skilled and talented development engineer. I have always seen a very positive and kind attitude in him and he has been of great help for the team and for me. I can truly say that he has endless potential in everything he does, starting from his innovative ideas to his actual hands-on stuff. He always likes to explore new things whenever possible, no matter how tough the tasks will be. Without any doubt, I conveniently believe that he is a key player towards any project he is involved in. His skill and personality have made him a good teammate who anyone would like to work with. I enjoyed working with him at ALAX, and can't wait for a chance to work with him again in the future.",
  },
  {
    name: 'Del Elson',
    role: 'VN Country Manager at Lingble',
    relationship: 'Del managed Van directly at ALAX',
    date: 'Sep 2019',
    quote:
      "Van is an exceptionally capable and skilled front end developer. He has worked on some complex code at ALAX using advanced methods such as reactive programming, SOLID techniques, and some complex JavaScript and TypeScript modules. He has also been working on code reviews and refactoring existing code and shown that he's a capable developer with a good eye for detail. He has also spent some time mentoring junior team members and interns and been able to develop leadership skills. Above and beyond that, Van is a good guy to work with, always helping out other team members with tasks, as well as contributing to the social life and overall harmony of the office. I recommend him highly.",
  },
  {
    name: 'Quang Ngo',
    role: 'Software Architect at Axon Active',
    relationship: 'Quang worked with Van on the same team',
    date: 'Jan 2021',
    quote:
      'Van Tran is a person who has a professional mindset in software development. He was a good support teammate and have strong skills in Front-end when working with me. He also ready to deal with difficult situations or techniques to solve the problem. He also is a polite and funny guy. Good teammate.',
  },
  {
    name: 'Hung Nguyen',
    role: 'Data-Driven Technology Leader · AI Integration Expert',
    relationship: 'Hung managed Van directly at ALAX',
    date: 'Sep 2019',
    quote:
      'Van is a young and promising front-end developer. When working at ALAX as a front-end developer, he managed to finish difficult tasks on time under minor supervision. He is open to feedbacks and willing to change to make himself better at the job. Van also possesses a good set of soft skills due to his educational background.',
  },
]

export interface ProductCase {
  id: string
  category: 'Product software' | 'AI product' | 'Side project'
  title: string
  context?: string
  period?: string
  maturity: string
  overview: string
  shipped: string
  outcome?: string
  decision?: string
  href?: string
  image?: string
  imageAlt?: string
  links?: { label: string; href: string }[]
  evidence?: {
    metrics: string
    note: string
    sources: { label: string; href: string }[]
  }
}

export interface WorkflowGroup {
  title: string
  description: string
  items: {
    name: string
    value: string
    maturity: string
  }[]
}

export interface ToolkitGroup {
  category: string
  description: string
  color: string
  tools: {
    name: string
    use: string
    logo: string
  }[]
}

export interface CareerRole {
  company: string
  title: string
  period: string
  focus: string
  highlights: string[]
  skills?: string[]
  techStack?: string[]
}

export interface OpenSourceProject {
  name: string
  description: string
  href: string
  traction?: string
  image?: string
}

export const productCases: ProductCase[] = [
  {
    id: 'lumberworks-platform',
    category: 'Product software',
    title: 'Prediction-market platform',
    context: 'Technical Product Manager → Head of Product · Lumberworks',
    period: 'Mar 2025 – Aug 2026',
    maturity: '0 → 1 · White-label solution',
    overview: 'A market experience for participants, with a shared backoffice for Operations, Support, and partners.',
    shipped: 'Built from 0 to 1 with 10 engineers and a Korean partner: trading, markets, users, funds, referrals, rewards, access controls, and analytics.',
    decision: 'Packaged the platform and backoffice as a reusable core for white-label delivery.',
    outcome: 'Delivered both the participant experience and the operational controls needed to run it.',
    href: 'https://xmarket.app/',
    image: '/evidence/xmarket.webp',
    imageAlt: 'Prediction-market platform',
  },
  {
    id: 'vietcetera-platform',
    category: 'Product software',
    title: 'Vietcetera website & operational platform',
    context: 'Technical Product Manager · Vietcetera Media',
    period: 'Apr 2020 – Mar 2025',
    maturity: 'Public website · Internal platform',
    overview: 'A reader-facing media website, connected to shared workflows for Editorial, Marketing, HR, Finance, and leadership.',
    shipped: 'Led the web client build from scratch and WordPress-to-Next.js migration, plus content, advertising, payment, recruitment, CRM, reporting, and editorial AI workflows.',
    decision: 'Connected the public reading experience with the internal publishing and business platform.',
    outcome: 'Helped grow the website from thousands to millions of views.',
    href: 'https://vietcetera.com/',
    image: '/evidence/vietcetera.webp',
    imageAlt: 'Vietcetera reader-facing media website',
    evidence: {
      metrics: '2.5M+ website pageviews · 24M+ monthly cross-platform audience · 171M+ social & web engagements',
      note: 'Company reach, not individually attributable results. Media kit: website pageview period unspecified; audience highlights, 2025.',
      sources: [
        { label: 'Official media kit', href: 'https://vietcetera.com/vn/media-kit' },
        { label: 'Public traffic proof', href: '/evidence/vietcetera-media-kit.webp' },
      ],
    },
  },
  {
    id: 'vietcetera-mobile',
    category: 'Product software',
    title: 'Vietcetera mobile app',
    context: 'Technical Product Manager · Vietcetera Media',
    period: 'Apr 2020 – Mar 2025',
    maturity: 'Built & launched · iOS / Android',
    overview:
      'A mobile reading experience bringing Vietcetera articles directly to readers.',
    shipped:
      'Led development from scratch through launch and distribution on the App Store and Google Play, with article reading, saved articles, and sharing.',
    outcome:
      'Launched a dedicated mobile reading experience on iOS and Android.',
    href: 'https://play.google.com/store/apps/details?id=com.vietcetera&hl=en&gl=VN',
    image: '/evidence/vietcetera-app.webp',
    imageAlt: 'Official Vietcetera app-store artwork showing reading, saved articles, and categories',
    evidence: {
      metrics: '50K+ Android downloads · Google Play 3.6/5 (1.52K reviews) · App Store VN 4.9/5 (6.2K ratings)',
      note: 'Store snapshot · 10 Oct 2026, after my tenure. Android downloads are a threshold; iOS downloads are not public. Official Google Play artwork.',
      sources: [
        { label: 'Google Play', href: 'https://play.google.com/store/apps/details?id=com.vietcetera&hl=en&gl=VN' },
        { label: 'App Store · Vietnam', href: 'https://apps.apple.com/vn/app/vietcetera/id1564202068' },
        { label: 'Android metric proof', href: '/evidence/vietcetera-google-play.webp' },
        { label: 'iOS metric proof', href: '/evidence/vietcetera-app-store.webp' },
      ],
    },
  },
  {
    id: 'quick',
    category: 'Product software',
    title: 'Quick',
    context: 'Frontend Engineer · BLISS',
    period: 'Feb 2017 – Aug 2018',
    maturity: 'Customer-facing e-commerce',
    overview: 'E-commerce for a Belgian food and beverage brand.',
    shipped: 'Built customer-facing e-commerce interfaces alongside web, mobile, and interactive experiences.',
    href: 'https://quick.be/',
    image: '/evidence/quick.webp',
    imageAlt: 'Quick e-commerce website',
  },
  {
    id: 'creative-ai-workspace',
    category: 'AI product',
    title: 'CreativeOps',
    context: 'Product work · Lumberworks',
    maturity: 'Product–market fit exploration',
    overview:
      'A brand-aligned image and video workspace for non-creative users, built at Lumberworks to explore product–market fit with clients in South Korea, Hong Kong, and the USA.',
    shipped:
      'Generation workflows with human approval, feedback, ratings, and voting.',
    decision: 'Used human approval and client feedback for subjective creative quality rather than relying on automated judging alone.',
    outcome: 'Feedback informed prompts and generation workflows.',
  },
  {
    id: 'multi-agent-evaluation',
    category: 'AI product',
    title: 'PlayStat · multi-agent analysis product',
    maturity: 'MVP & client demo',
    overview:
      'Led the product end to end, from ideation and AI research through MVP delivery and client demos.',
    shipped:
      'A LangGraph-based multi-agent system, including model selection, agent behavior, tool calls, data requirements, workflows, business logic, and evaluation.',
    outcome:
      'Moved the concept from research into a working MVP that could be demonstrated to clients.',
    decision: 'Used a synthetic golden dataset and LLM-as-a-judge evaluation to test crypto and prediction-market analysis.',
  },
  {
    id: 'creativeops-cai-lo-nuong',
    category: 'Side project',
    title: 'Cái lò nướng - genAI workflow',
    context: 'Independent product builder · Client: Cái Lò Nướng',
    maturity: 'Internal creative workflow',
    overview: 'An internal image-creation system that helps Operations turn raw cake photos into creative assets aligned with the Creative Director’s taste and visual direction.',
    shipped: 'Built a workflow for raw product photos, reusable creative briefs, brand guidelines, and an asset library with approval status.',
    decision: 'Made brand guidelines and reusable briefs part of the workflow so Operations can create assets within the Creative Director’s visual direction.',
    outcome: 'Designed to reduce repetitive production work for designers and help Operations create everyday assets quickly from raw cake photos.',
    image: '/evidence/creativeops-assets.webp',
    imageAlt: 'Cái Lò Nướng asset library showing cake and dessert images with approval status',
    evidence: {
      metrics: 'Asset library · Reusable brief templates · Brand guidelines',
      note: 'Owner-supplied screens · 16 Oct 2026. Screens show workflow examples, not measured time savings. Account panel excluded.',
      sources: [
        { label: 'Asset library', href: '/evidence/creativeops-assets.webp' },
        { label: 'Brief templates', href: '/evidence/creativeops-briefs.webp' },
        { label: 'Brand guidelines', href: '/evidence/creativeops-brands.webp' },
      ],
    },
  },
  {
    id: '96booth',
    category: 'Side project',
    title: '96booth ecosystem',
    context: 'Independent product builder',
    maturity: 'Operational product · Internal ERP',
    overview: 'An Electron photobooth experience for customers and a remote operations platform for booth operators in Saigon.',
    shipped: 'Photo capture, galleries, downloads, SePay payments, monitoring, operator alerts, and AI-assisted log anomaly reporting. The backoffice includes booth management, crash logs, remote settings, photostrip frames, and a custom template editor.',
    decision: 'Connected customer photo sessions and payments with remote booth configuration and support.',
    outcome: '2 active booths as of October 2026, owner-reported. Operators manage the system remotely; staff sign-in required.',
    href: 'https://www.96photoboothco.vn/',
    image: '/evidence/96booth-admin.webp',
    imageAlt: '96booth admin dashboard with booth, frame, session, and print reporting',
    links: [
      { label: 'Business website', href: 'https://www.96photoboothco.vn/' },
      { label: 'Operations ERP · staff sign-in', href: 'https://app.96photoboothco.vn/' },
      { label: 'Customer website screenshot', href: '/evidence/96booth.webp' },
    ],
    evidence: {
      metrics: 'Dashboard · Frame library · Template editor · SePay integration',
      note: 'Owner-supplied screens · 10 Oct 2026. Account details, bank accounts, and QR content removed. Dashboard status is a snapshot.',
      sources: [
        { label: 'Dashboard', href: '/evidence/96booth-admin.webp' },
        { label: 'Frame library', href: '/evidence/96booth-frames.webp' },
        { label: 'Template editor', href: '/evidence/96booth-editor.webp' },
        { label: 'SePay integration', href: '/evidence/96booth-sepay.webp' },
      ],
    },
  },
  {
    id: 'lekha',
    category: 'Side project',
    title: 'Lekha',
    context: 'Independent product builder',
    maturity: 'Live e-commerce product',
    overview: 'A shopping platform with customer support and AI-assisted product discovery.',
    shipped: 'Built the storefront, support experience, and product-discovery workflow.',
    href: 'https://lekha.vn/en',
    image: '/evidence/lekha.webp',
    imageAlt: 'Lekha e-commerce storefront',
  },
  {
    id: 'clinicbook',
    category: 'Side project',
    title: 'ClinicBook · Bách Di Đường ERP',
    context: 'Independent product builder · Internal SME system',
    maturity: 'Operational product · Staff access',
    overview: 'One internal system for clinic staff to coordinate appointments, customers, and business operations.',
    shipped: 'Built booking management, CRM, revenue reporting, orders, and drug management.',
    decision: 'Brought scheduling and business operations into a shared ERP.',
    href: 'https://bachdiduong-booking.pages.dev/bookings',
    image: '/evidence/clinicbook.webp',
    imageAlt: 'Owner-supplied aggregate clinic reporting, cropped to exclude patient records',
    links: [
      { label: 'Clinic ERP · staff sign-in', href: 'https://bachdiduong-booking.pages.dev/bookings' },
      { label: 'Aggregate reporting screenshot', href: '/evidence/clinicbook.webp' },
    ],
  },
  {
    id: 'roastmap',
    category: 'Side project',
    title: 'Roastmap',
    context: 'Independent product builder',
    maturity: 'Built & launched',
    overview: 'A lightweight visual roadmap planner for turning plans into clear timelines.',
    shipped: 'Drag-and-drop timelines, local editing, JSON export, and optional cloud sharing.',
    decision: 'Kept editing local with portable JSON data and optional sharing.',
    href: 'https://roastmap.cc/',
    image: '/evidence/roastmap.webp',
    imageAlt: 'Roastmap visual roadmap planner',
  },
  {
    id: 'sme-advisory',
    category: 'Side project',
    title: 'SME advisory',
    context: 'Independent advisory',
    maturity: 'Advisory · Not a software product',
    overview: 'Technical and product support for entrepreneur networks and small businesses.',
    shipped: 'Operational discovery, system audits, AI automation guidance, technical direction, and build-versus-buy recommendations.',
  },
]

export const agentProjects: {
  name: string
  label: string
  description: string
  steps: string[]
  href?: string
}[] = [
  {
    name: 'Meeting transcript agent',
    label: 'Ontology-based',
    description: 'An agent that uses an ontology to organize knowledge from meeting transcripts.',
    steps: ['Transcript', 'Ontology', 'Knowledge'],
  },
  {
    name: 'ScrumClaw',
    label: 'Hermes · Personal use',
    description: 'My Hermes-based agent for managing the team backlog and keeping track of status.',
    steps: ['Backlog', 'Track', 'Status'],
  },
  {
    name: 'MarketingClaw',
    label: 'R&D · Email campaigns',
    description: 'An experimental email campaign agent designed to work across marketing tools and learn from feedback. Not production campaign automation.',
    steps: ['Query audience', 'HTML email', 'Learn'],
  },
  {
    name: 'Content engine claw',
    label: 'Hermes · Threads',
    description: 'A Hermes-based agent that runs and manages posts for my Threads channel with 2.7k followers.',
    steps: ['Content', 'Posts', 'Manage'],
  },
]

export const agentResearch = [
  { name: 'Ontology', description: 'How to structure knowledge agents can use.' },
  { name: 'Agent harnesses', description: 'How to give agents the context, tools, and constraints they need.' },
  { name: 'Long-running work', description: 'How to make agents more reliable over longer tasks.' },
  { name: 'Better evals', description: 'Learning how to test agent behavior and judge output quality.' },
]

export const productWorkflow = [
  { task: 'Discovery', before: 'Research and stakeholder alignment', withAI: 'AI-assisted research and synthesis' },
  { task: 'Definition', before: 'Backlog refinement, PRDs, and user stories', withAI: 'AI-assisted drafts and working prototypes' },
  { task: 'Delivery', before: 'Engineering coordination, stand-ups, and reporting', withAI: 'AI-assisted stand-up preparation and weekly reports' },
] as const

export const workflowGroups: WorkflowGroup[] = [
  {
    title: 'Client workflows',
    description: 'Build around the user’s real context and decision point.',
    items: [
      {
        name: 'Retail email planning',
        value: 'Gather context, plan, retrieve warehouse data through MCP, and draft for the intended audience.',
        maturity: 'Pilot',
      },
      {
        name: 'Retail, reporting, and marketing plugins',
        value: 'Co-designed Claude plugins with retail clients, connecting warehouse data through MCP with defined rules and skills.',
        maturity: 'Client work',
      },
    ],
  },
  {
    title: 'Product and engineering operations',
    description: 'Reduce coordination cost without hiding decisions from teams.',
    items: [
      {
        name: 'Release communication',
        value: 'Track releases and explain features and user impact to teams, including Marketing.',
        maturity: 'Internal workflow',
      },
      {
        name: 'Issue-context triage',
        value: 'Summarize PostHog logs and send useful issue context to engineering channels.',
        maturity: 'Internal workflow',
      },
      {
        name: 'Market operations',
        value: 'Support resolution work and identify trends from X and Reddit for market ideas.',
        maturity: 'Built workflow',
      },
    ],
  },
  {
    title: 'Team knowledge',
    description: 'Make context legible to both people and agents.',
    items: [
      {
        name: 'Local-file-first knowledge system',
        value: 'Organize product, project, decision, and timeline knowledge with a clear ontology for agent questions.',
        maturity: 'Internal workflow',
      },
      {
        name: 'Meeting knowledge capture',
        value: 'Summarize transcripts, extract key points, and save structured notes to Notion.',
        maturity: 'Internal workflow',
      },
    ],
  },
  {
    title: 'Research & experiments',
    description: 'Research uncertain opportunities before making production claims.',
    items: [
      {
        name: 'Natural language to SQL',
        value: 'Explore warehouse questions through generated SQL and reviewable query results.',
        maturity: 'R&D',
      },
      {
        name: 'Campaign learning loops',
        value: 'Experiment with Hermes marketing workflows and autonomous email/social campaign review. No production campaign automation.',
        maturity: 'R&D',
      },
    ],
  },
]

export const toolkitGroups: ToolkitGroup[] = [
  {
    category: 'Build',
    description: 'Agents and the models behind them',
    color: '#f27bb8',
    tools: [
      {
        name: 'Claude Code',
        use: 'Builds and edits code and my ideas',
        logo: '/tool-logos/claude.svg',
      },
      {
        name: 'Codex',
        use: 'A second pair of eyes on code and reviews',
        logo: '/tool-logos/codex.svg',
      },
      {
        name: 'Cursor',
        use: 'Editor and agents for quick, hands-on changes',
        logo: '/tool-logos/cursor.png',
      },
      {
        name: 'OpenRouter',
        use: 'One key to test and compare models',
        logo: '/tool-logos/openrouter.png',
      },
    ],
  },
  {
    category: 'Research',
    description: 'Thinking, writing, web, UI patterns, transcripts',
    color: '#69c9f5',
    tools: [
      {
        name: 'Claude',
        use: 'Thinking partner',
        logo: '/tool-logos/claude.svg',
      },
      {
        name: 'ChatGPT',
        use: 'Quick answers and a second opinion',
        logo: '/tool-logos/chatgpt.png',
      },
      {
        name: 'Firecrawl',
        use: 'Turns websites into clean text for AI',
        logo: '/tool-logos/firecrawl.png',
      },
      {
        name: 'Mobbin',
        use: 'Real app screens and flows for reference',
        logo: '/tool-logos/mobbin.png',
      },
      {
        name: 'Hermes',
        use: 'Self-hosted tasks and transcriptions',
        logo: '/tool-logos/hermes.png',
      },
    ],
  },
  {
    category: 'Design & prototype',
    description: 'Screens, images, and video',
    color: '#f4f4f5',
    tools: [
      {
        name: 'Figma',
        use: 'Where the design lives',
        logo: '/tool-logos/figma.png',
      },
      {
        name: 'Midjourney',
        use: 'Moodboards and visual directions',
        logo: '/tool-logos/midjourney.png',
      },
      {
        name: 'Remotion',
        use: 'Motion and video, written as code',
        logo: '/tool-logos/remotion.png',
      },
    ],
  },
  {
    category: 'Productivity',
    description: 'Meetings, notes, and capture',
    color: '#76d8aa',
    tools: [
      {
        name: 'Granola',
        use: 'The AI notepad',
        logo: '/tool-logos/granola.png',
      },
      {
        name: 'CleanShot X',
        use: 'Screenshots and recordings for feedback',
        logo: '/tool-logos/cleanshot.png',
      },
      {
        name: 'Obsidian',
        use: 'My notes and second brain',
        logo: '/tool-logos/obsidian.png',
      },
    ],
  },
  {
    category: 'Analytics & tests',
    description: 'Product analytics and browser tests',
    color: '#c58bdf',
    tools: [
      {
        name: 'PostHog',
        use: 'Product analytics',
        logo: '/tool-logos/posthog.svg',
      },
      {
        name: 'Playwright',
        use: 'Tests and screenshots in a real browser',
        logo: '/tool-logos/playwright.png',
      },
    ],
  },
]

export const career: CareerRole[] = [
  {
    company: 'Lumberworks',
    title: 'Head of Product',
    period: 'Jan – Aug 2026',
    focus: 'Multi-product ownership, client delivery, and AI adoption.',
    highlights: [
      'Owned multi-product roadmaps and backlog priorities; aligned Marketing, Operations, and engineering around delivery goals.',
      'Tracked delivery progress, team capacity, and project health to support roadmap decisions.',
      'Worked with leadership and clients on demos, requirements, white-label solutions, and AI evaluation.',
      'Co-designed AI-assisted marketing and reporting workflows with retail clients around their business needs.',
      'Established shared onboarding, policies, company knowledge, and team guidelines across departments.',
    ],
    skills: ['Product leadership', 'Team mentoring', 'Market & competitor research', 'Product definition', 'Client collaboration', 'Roadmap management', 'Backlog prioritization', 'Stakeholder alignment'],
  },
  {
    company: 'Lumberworks',
    title: 'Technical Product Manager',
    period: 'Mar – Dec 2025',
    focus: '0-to-1 product delivery, partner collaboration, and operational workflows.',
    highlights: [
      'Led product tracks from 0 to 1 with the internal team, 10 engineers, and a Korean partner.',
      'Owned platform and backoffice workflows across trading, operations, growth, funds, and access control.',
      'Defined product reporting for trading, acquisition, retention, and operations to inform priorities.',
      'Worked with engineers to deliver deposit and withdrawal monitoring for fund operations.',
    ],
    skills: ['User discovery', 'Market & competitor research', 'Product definition', 'Workflow definition', 'Partner collaboration', 'Roadmap management', 'Backlog management', 'Engineering collaboration', 'Product analytics'],
  },
  {
    company: 'Vietcetera Media',
    title: 'Technical Product Manager',
    period: 'Apr 2020 – Mar 2025',
    focus: 'Reader experience, internal workflows, client delivery, and mobile product launches.',
    highlights: [
      'Led the website rebuild and publishing-platform migration; helped grow the website from thousands to millions of views. Public media kit: 2.5M+ website pageviews.',
      'Owned shared platforms across Editorial, Marketing, HR, Finance, and leadership.',
      'Led client websites, interactive campaigns, and mobile/event experiences.',
      'Led Vietcetera’s mobile app from product definition through engineering delivery, launch on iOS and Android, and performance reporting to leadership.',
      'Owned B2B campaign KPI reporting to support client and business decisions.',
      'Analyzed funnels and user behavior to understand the reader experience.',
      'Established shared onboarding, policies, company knowledge, and team guidelines across departments.',
    ],
    skills: ['User discovery', 'Market & competitor research', 'Product definition', 'Stakeholder management', 'Client collaboration', 'Roadmap management', 'Backlog prioritization', 'Engineering collaboration', 'Product analytics', 'Launch management'],
  },
  {
    company: 'Axon Active',
    title: 'Senior Software Engineer',
    period: 'Oct 2019 – Apr 2020',
    focus: 'Frontend-led delivery of Carfin.ch, a B2B fintech SaaS for end-to-end car-loan deal management for a Swiss client.',
    highlights: [
      'Built customer-facing web clients and admin tools for complex car-loan workflows using React and Redux.',
      'Led the frontend team in gathering requirements and coordinating delivery with the client-side Scrum Master.',
      'Contributed to the client’s charity and stock-portfolio analysis projects.',
    ],
    techStack: ['JavaScript', 'React.js', 'Redux', 'Python', 'Java / Spring Boot (backend)', 'AWS'],
  },
  {
    company: 'ALAX.io',
    title: 'Senior Software Engineer',
    period: 'Aug 2018 – Oct 2019',
    focus: 'Game marketplace clients, admin backoffice, and publisher tools alongside DApps and smart-contract integrations.',
    highlights: [
      'Built both the customer-facing client and admin backoffice for marketplace operations.',
      'Built a game-publisher portal for uploading and managing games, payments, and assets.',
      'Supported marketing websites and landing pages powered by Strapi CMS.',
    ],
    techStack: ['React.js', 'Redux-Saga', 'TypeScript', 'Material UI', 'React Hook Form', 'React i18n', 'Strapi', 'GCP'],
  },
  {
    company: 'BLISS',
    title: 'Frontend Engineer',
    period: 'Feb 2017 – Aug 2018',
    focus: 'E-commerce, mobile applications, and lightweight creative web experiences.',
    highlights: [
      'Built customer-facing e-commerce interfaces for Quick.be.',
      'Built the Homey mobile app with React Native.',
      'Created creative HTML/CSS email templates and lightweight website experiences with vanilla JavaScript, prioritizing fast loading.',
    ],
    techStack: ['HTML', 'JavaScript', 'CSS', 'React.js', 'React Native', 'PHP', 'Laravel', 'Vue.js', 'Python', 'Swift'],
  },
]

export const openSourceProjects: OpenSourceProject[] = [
  {
    name: 'pm-kit',
    description: 'An open-source workspace for product specs, decisions, status, and knowledge used with coding agents.',
    href: 'https://github.com/kv0906/pm-kit',
    traction: '130+ GitHub stars · 30+ forks',
    image: '/evidence/pm-kit.webp',
  },
  {
    name: 'gdd-skills',
    description: 'Validated Goal Contracts that keep autonomous Codex workflows inside human-approved boundaries.',
    href: 'https://github.com/kv0906/gdd-skills',
  },
  {
    name: 'rkg',
    description: 'React and Next.js dependency graphs in Neo4j, queried through AI-powered MCP tools.',
    href: 'https://github.com/kv0906/rkg',
  },
  {
    name: 'cc-skills',
    description: 'A personal collection of curated and self-developed Claude Code skills for coding, planning, diagrams, and creative work.',
    href: 'https://github.com/kv0906/cc-skills',
  },
]

export const practiceAreas = [
  ['Product discovery and systems thinking', 'Map stakeholders, use cases, workflows, and technical constraints.'],
  ['Market & competitor research', 'Research market trends and competitors to identify product opportunities and inform priorities.'],
  ['Product definition', 'Turn ambiguous problems into clear product concepts, user flows, and actionable features.'],
  ['UI/UX and prototyping', 'Use Figma, frontend code, and high-fidelity prototypes to make decisions concrete.'],
  ['Hands-on implementation', 'Apply a software engineering background and coding agents when working prototypes reduce uncertainty.'],
  ['AI-native product work', 'Build a harness: the context, tools, and steps that help agents research, prototype, and support delivery.'],
  ['Client collaboration', 'Work directly through requirements, preferences, demos, prototype agreement, and engineering delivery.'],
  ['Evaluation', 'Keep objective agent evals distinct from human feedback loops for subjective creative work.'],
] as const

export const verifiedCredentials = [
  'Professional Scrum Master I (PSM I)',
  'Professional Scrum Product Owner I (PSPO I)',
  'Chainlink Fundamentals',
  'Claude Code in Action',
]

export const teaching = {
  chainlink: {
    title: 'Chainlink Community Advocate',
    description:
      'Research emerging blockchain infrastructure, share practical knowledge, and help host workshops and community discussions with Chainlink.',
    images: [
      {
        src: '/evidence/chainlink-panel.webp',
        alt: 'Blockchain and DeFi panel discussion at a Chainlink community event',
      },
      {
        src: '/evidence/chainlink-community.webp',
        alt: 'Group photo from a Chainlink community event',
      },
      {
        src: '/evidence/chainlink-workshop.webp',
        alt: 'Blockchain workshop presentation at a Chainlink community event',
      },
    ],
  },
  program: {
    title: 'Coach, mentor, and judge · Vingroup AI20K Program',
    description:
      'Coached participants, mentored teams, and evaluated competition submissions. The program scale is 20,000 participants; it is not an individual teaching count.',
    href: 'https://tuoitre.vn/vingroup-dao-tao-20-000-nhan-tai-ai-thuc-chien-gop-phan-phat-trien-nguon-nhan-luc-cong-nghe-cao-100260729070137888.htm',
  },
  participation: {
    title: 'AI20K participation reference',
    description: 'User-supplied LinkedIn reference for the coaching, mentoring, and judging role.',
    href: 'https://www.linkedin.com/in/tran-huu-khanh-van/overlay/Position/3022011066/treasury?profileId=ACoAAB1WFScBVJuPMhJzKIjzn52imwEEf2SrJLc',
  },
  publication: {
    title: 'How Modern PMs Work with AI Agents',
    description: 'A free mini course on practical product work with AI agents.',
    href: 'https://drive.google.com/drive/folders/1biRGIg-fq5PpIgt-M3bBdzq5CdS8bjxI?usp=sharing',
  },
}
